import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  getTimeTable, 
  updateTimeTable, 
  addTimeTableEntry, 
  updateTimeTableEntry, 
  deleteTimeTableEntry,
  TimeEntry, 
  TimeTableResponse 
} from '../../../../../services/eventService';

interface TimeTableModalProps {
  show: boolean;
  onClose: () => void;
  onTimeTableUpdate?: (count: number) => void;
}

const TimeTableModal: React.FC<TimeTableModalProps> = ({
  show,
  onClose,
  onTimeTableUpdate
}) => {
  const { eventId } = useParams<{ eventId: string }>();
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<{ show: boolean; entryId: string | null }>({ 
    show: false, 
    entryId: null 
  });
  
  // Form state
  const [formData, setFormData] = useState<TimeEntry>({
    startTime: '',
    endTime: '',
    type: '',
    description: ''
  });

  // Load timetable data when modal opens
  useEffect(() => {
    if (show && eventId) {
      loadTimeTable();
    }
  }, [show, eventId]);

  const loadTimeTable = async () => {
    if (!eventId) return;
    
    setLoading(true);
    try {
      const response = await getTimeTable(eventId);
      // Sort entries by start time ascending
      const sortedEntries = response.entries.sort((a, b) => 
        a.startTime.localeCompare(b.startTime)
      );
      setTimeEntries(sortedEntries);
      if (onTimeTableUpdate) {
        onTimeTableUpdate(sortedEntries.length);
      }
    } catch (error) {
      console.error('Error loading timetable:', error);
      toast.error('Failed to load time schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eventId) return;

    if (!formData.startTime || !formData.endTime || !formData.type) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      if (editingEntry && editingEntry.id) {
        // Update existing entry
        await updateTimeTableEntry(eventId, editingEntry.id, formData);
        toast.success('Schedule entry updated successfully');
      } else {
        // Add new entry
        await addTimeTableEntry(eventId, formData);
        toast.success('Schedule entry added successfully');
      }
      
      // Reset form and reload data
      resetForm();
      await loadTimeTable();
    } catch (error) {
      console.error('Error saving entry:', error);
      toast.error('Failed to save schedule entry');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (entry: TimeEntry) => {
    setEditingEntry(entry);
    setFormData({
      startTime: entry.startTime,
      endTime: entry.endTime,
      type: entry.type,
      description: entry.description
    });
  };

  const handleDelete = async (entryId: string) => {
    if (!eventId) return;

    setShowDeleteConfirm({ show: true, entryId });
  };

  const confirmDelete = async () => {
    const { entryId } = showDeleteConfirm;
    if (!eventId || !entryId) return;

    try {
      await deleteTimeTableEntry(eventId, entryId);
      toast.success('Schedule entry deleted successfully');
      await loadTimeTable();
      setShowDeleteConfirm({ show: false, entryId: null });
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete schedule entry');
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm({ show: false, entryId: null });
  };

  const resetForm = () => {
    setFormData({
      startTime: '',
      endTime: '',
      type: '',
      description: ''
    });
    setEditingEntry(null);
  };

  const getTypeBadgeClass = (type: string) => {
    switch (type) {
      case 'round': return 'badge-round bg-primary';
      case 'break': return 'badge-break bg-secondary';
      case 'welcome': return 'badge-welcome bg-success';
      case 'closing': return 'badge-closing bg-danger';
      case 'other': return 'badge-other bg-purple';
      default: return 'bg-secondary';
    }
  };

  if (!show) return null;

  return (
    <>
      <style>{`
        .schedule-entry-item {
          display: flex;
          align-items: center;
          padding: 12px 15px;
          margin-bottom: 8px;
          border-radius: 6px;
          background-color: #f8f9fa;
          border: 1px solid #e9ecef;
          transition: all 0.2s ease;
        }
        
        .schedule-entry-item:hover {
          background-color: #e9ecef;
          transform: translateY(-1px);
          box-shadow: 0 2px 5px rgba(0,0,0,0.08);
        }
        
        .schedule-entry-item.selected {
          background-color: #e7f0fd;
          border-color: #b9d4ff;
        }
        
        .schedule-entry-time {
          min-width: 130px;
          font-weight: 500;
          font-size: 0.9rem;
        }
        
        .schedule-entry-type {
          width: 90px;
          text-align: center;
        }
        
        .schedule-entry-type .badge {
          width: 80px;
          font-size: 0.75rem;
        }
        
        .schedule-entry-desc {
          flex-grow: 1;
          margin: 0 15px;
          font-size: 0.9rem;
        }
        
        .schedule-entry-actions {
          display: flex;
          gap: 5px;
        }
        
        .badge-round { background-color: #0d6efd !important; }
        .badge-break { background-color: #6c757d !important; }
        .badge-welcome { background-color: #198754 !important; }
        .badge-closing { background-color: #dc3545 !important; }
        .badge-other { background-color: #6f42c1 !important; }
        
        .empty-schedule-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          color: #6c757d;
        }
      `}</style>
      
      <div className="modal show d-block" tabIndex={-1}>
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header bg-light">
              <h5 className="modal-title">
                <i className="bi bi-calendar2-week me-2"></i> Time Schedule for Event
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div className="row">
                  {/* Left side - Schedule entries (2/3 width) */}
                  <div className="col-md-8 pe-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6>Schedule Entries ({timeEntries.length})</h6>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={loadTimeTable}
                        disabled={loading}
                      >
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Refresh
                      </button>
                    </div>
                    
                    {timeEntries.length === 0 ? (
                      <div className="empty-schedule-state text-center py-5">
                        <i className="bi bi-calendar-x fs-1 text-muted"></i>
                        <p className="text-muted mt-3">No schedule entries yet. Add your first entry using the form.</p>
                      </div>
                    ) : (
                      <div className="schedule-entries">
                        {timeEntries.map((entry, index) => (
                          <div 
                            key={entry.id || index}
                            className={`schedule-entry-item ${editingEntry?.id === entry.id ? 'selected' : ''}`}
                          >
                            <div className="schedule-entry-time">
                              <strong>{entry.startTime} - {entry.endTime}</strong>
                            </div>
                            <div className="schedule-entry-type">
                              <span className={`badge ${getTypeBadgeClass(entry.type)}`}>
                                {entry.type}
                              </span>
                            </div>
                            <div className="schedule-entry-desc">
                              {entry.description}
                            </div>
                            <div className="schedule-entry-actions">
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => handleEdit(entry)}
                                title="Edit"
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              {entry.id && (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleDelete(entry.id!)}
                                  title="Delete"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Right side - Entry form (1/3 width) */}
                  <div className="col-md-4 border-start ps-4">
                    <h6>
                      {editingEntry ? 'Edit Entry' : 'Add New Entry'}
                      {editingEntry && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm ms-2"
                          onClick={resetForm}
                        >
                          Cancel Edit
                        </button>
                      )}
                    </h6>
                    <form onSubmit={handleSubmit} className="row g-2">
                      <div className="col-12 mb-3">
                        <label htmlFor="startTime" className="form-label">Start Time *</label>
                        <input 
                          type="time" 
                          className="form-control" 
                          id="startTime" 
                          value={formData.startTime}
                          onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                          required 
                        />
                      </div>
                      <div className="col-12 mb-3">
                        <label htmlFor="endTime" className="form-label">End Time *</label>
                        <input 
                          type="time" 
                          className="form-control" 
                          id="endTime" 
                          value={formData.endTime}
                          onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
                          required 
                        />
                      </div>
                      <div className="col-12 mb-3">
                        <label htmlFor="entryType" className="form-label">Type *</label>
                        <select 
                          className="form-select" 
                          id="entryType" 
                          value={formData.type}
                          onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                          required
                        >
                          <option value="">Select type...</option>
                          <option value="round">Round</option>
                          <option value="break">Break</option>
                          <option value="welcome">Welcome</option>
                          <option value="closing">Closing</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div className="col-12 mb-3">
                        <label htmlFor="entryDescription" className="form-label">Description</label>
                        <input 
                          type="text" 
                          className="form-control" 
                          id="entryDescription" 
                          placeholder="Description" 
                          value={formData.description}
                          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        />
                      </div>
                      <div className="col-12">
                        <button 
                          type="submit" 
                          className="btn btn-primary w-100"
                          disabled={saving}
                        >
                          {saving ? (
                            <>
                              <div className="spinner-border spinner-border-sm me-2" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                              {editingEntry ? 'Updating...' : 'Adding...'}
                            </>
                          ) : (
                            <>
                              <i className={`bi ${editingEntry ? 'bi-check-circle' : 'bi-plus-circle'} me-1`}></i>
                              {editingEntry ? 'Update Entry' : 'Add Entry'}
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm.show && (
        <div className="modal show d-block" tabIndex={-1} style={{ zIndex: 1060 }}>
          <div className="modal-dialog modal-sm">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-exclamation-triangle text-warning me-2"></i>
                  Confirm Delete
                </h5>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this schedule entry?</p>
                <p className="text-muted small">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={cancelDelete}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={confirmDelete}
                >
                  <i className="bi bi-trash me-1"></i>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TimeTableModal;
